import { createContext } from 'preact';
import { useContext, useEffect, useMemo, useState } from 'preact/hooks';
import { openDb, getAll } from './store/db.js';
import { startSync } from './store/sync.js';

export const SessionContext = createContext(null);
export const DBContext = createContext(null);
export const SyncContext = createContext(null);

export function useSession() {
  return useContext(SessionContext);
}
export function useDb() {
  return useContext(DBContext);
}
export function useSync() {
  return useContext(SyncContext);
}

export function Providers({ children }) {
  const [db, setDb] = useState(null);
  const [session, setSession] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('sps:session') || 'null');
    } catch {
      return null;
    }
  });
  const [sync, setSync] = useState({
    status: 'offline',
    pending: 0,
    lastSyncedAt: null,
    conflicts: 0,
    kickNow: () => {},
  });

  useEffect(() => {
    openDb().then(setDb);
  }, []);
  useEffect(() => {
    if (!db || !session?.token) return;

    // The pill reports what is actually in the local stores, not what the last
    // drain happened to return: an item that failed to send is still pending.
    const recount = async () => {
      const pending = (await getAll(db, 'outbox')).length;
      const conflicts = (await getAll(db, 'handovers')).filter(
        (h) => h.local_status === 'conflict',
      ).length;
      setSync((s) => ({ ...s, pending, conflicts }));
    };

    const emit = (type) => {
      if (type === 'sync:done') {
        setSync((s) => ({
          ...s,
          status: navigator.onLine ? 'online' : 'offline',
          lastSyncedAt: new Date().toISOString(),
        }));
        recount();
      } else if (type === 'sync:error') {
        setSync((s) => ({ ...s, status: navigator.onLine ? 'error' : 'offline' }));
        recount();
      } else if (type === 'outbox:changed') {
        recount();
      }
    };

    const onNetwork = () =>
      setSync((s) => ({ ...s, status: navigator.onLine ? s.status : 'offline' }));
    addEventListener('online', onNetwork);
    addEventListener('offline', onNetwork);

    const { stop, kick } = startSync(db, session.token, { emit });
    setSync((s) => ({ ...s, status: navigator.onLine ? s.status : 'offline', kickNow: kick }));
    recount();
    return () => {
      removeEventListener('online', onNetwork);
      removeEventListener('offline', onNetwork);
      stop();
    };
  }, [db, session?.token]);

  const applySession = (s) => {
    if (s) localStorage.setItem('sps:session', JSON.stringify(s));
    else localStorage.removeItem('sps:session');
    setSession(s);
  };
  const clearSession = () => applySession(null);

  const value = useMemo(() => ({ ...session, setSession: applySession, clearSession }), [session]);
  if (!db) return null;
  return (
    <SessionContext.Provider value={value}>
      <DBContext.Provider value={{ db }}>
        <SyncContext.Provider value={sync}>{children}</SyncContext.Provider>
      </DBContext.Provider>
    </SessionContext.Provider>
  );
}
